import { UserRepository } from "../../domain/repository/UserRepository";
import { Either, isRight, Left, Right } from "../../core/types/Either";
import { UserModel } from "../model/UserModel";
import { UserRemoteDataSource } from "../dataSource/UserRemoteDataSource";
import { UserLocalDataSource } from "../dataSource/UserLocalDataSource";
import { JwtHelper } from "../../core/helpers/JwtHelper";
import { SessionExpiredError } from "../../core/error/error";

export class UserRepositoryImpl extends UserRepository {
  remoteDataSource: UserRemoteDataSource;
  localDataSource: UserLocalDataSource;
  jwtHelper: JwtHelper;

  constructor(
    remoteDataSource: UserRemoteDataSource,
    localDataSource: UserLocalDataSource,
    jwtHelper: JwtHelper
  ) {
    super();
    this.remoteDataSource = remoteDataSource;
    this.localDataSource = localDataSource;
    this.jwtHelper = jwtHelper;
  }

  async login(email: string, password: string): Promise<Either<Error, string>> {
    try {
      return Right(
        await this.remoteDataSource.login(email, password).then((result) => {
          this.localDataSource.setToken(result);
          return result;
        })
      );
    } catch (e) {
      return Left(e);
    }
  }

  async getUser(): Promise<Either<Error, UserModel>> {
    try {
      const token = this.localDataSource.getToken();
      if (!this.jwtHelper.isTokenAlive(token)) {
        return Left(new SessionExpiredError());
      }
      // PPI-1848: persist whatever token the auth service actually answered
      // with — rotated when near expiry, unchanged otherwise — instead of
      // re-persisting the one this call was made with. Discarding the
      // rotation is what made sliding renewal a no-op for any member who
      // never revisited the login page.
      const session = await this.remoteDataSource.getUserByToken(token);
      this.localDataSource.setToken(session.token);
      return Right(session.user);
    } catch (e) {
      return Left(e);
    }
  }

  async getUserByToken(token: string): Promise<Either<Error, UserModel>> {
    try {
      if (!this.jwtHelper.isTokenAlive(token)) {
        return Left(new SessionExpiredError());
      }
      const session = await this.remoteDataSource.getUserByToken(token);
      this.localDataSource.setToken(session.token);
      return Right(session.user);
    } catch (e) {
      return Left(e);
    }
  }

  async logout(): Promise<Either<Error, void>> {
    try {
      this.remoteDataSource.logout();
      return Right(this.localDataSource.unsetToken());
    } catch (e) {
      return Left(e);
    }
  }

  async getTokenRemainingSeconds(): Promise<Either<Error, number>> {
    try {
      const token = this.localDataSource.getToken();
      return Right(this.jwtHelper.getSecondsUntilExpiration(token));
    } catch (e) {
      return Left(e);
    }
  }

  /**
   * Reuses getUser's rotated-token persistence and discards the User, rather
   * than duplicating the call-and-persist logic. One place decides how a
   * rotated token is adopted.
   */
  async renewSession(): Promise<Either<Error, void>> {
    const result = await this.getUser();
    return isRight(result) ? Right(undefined) : Left(result.value);
  }
}
